/* global SideDrawer */

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'trekker-garage';
const CACHE_KEY   = 'trekker-vehicle-data';
const CACHE_TTL   = 60 * 60 * 1000; // 1 hour in ms
const MAX_VEHICLES = 5;

// Tag format on Shopify products: fit:YEAR:MAKE-SLUG:MODEL-SLUG
// e.g.  fit:2023:toyota:tundra
//       fit:2023:ineos-grenadier:grenadier
//       fit:2022:land-rover:defender
const FIT_PREFIX = 'fit:';

// ─── Display name helpers ─────────────────────────────────────────────────────

/**
 * Convert a tag slug to a human-readable display name.
 * "land-rover" → "Land Rover", "ineos-grenadier" → "Ineos Grenadier"
 * Override specific slugs in the BRAND_NAMES map for proper capitalisation.
 */
const BRAND_NAMES = {
  'bmw':   'BMW',
  'gmc':   'GMC',
  'ram':   'RAM',
  'kia':   'KIA',
  'ineos-grenadier': 'INEOS Grenadier',
  'land-rover': 'Land Rover',
  'mercedes-benz': 'Mercedes-Benz',
  'alfa-romeo': 'Alfa Romeo',
};

function toDisplayName(slug) {
  if (BRAND_NAMES[slug]) return BRAND_NAMES[slug];
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function toSlug(name) {
  return name.toLowerCase().replace(/\s+/g, '-');
}

// ─── Vehicle data — Shopify Storefront API (vehicle_fitment metaobjects) ──────
//
// Metaobject type: vehicle_fitment
// Fields: year, make, make_slug, model, model_slug, collection (collection ref)
//
// Hierarchy shape:
//   {
//     "2023": {
//       "toyota": {
//         name: "Toyota",
//         models: {
//           "tundra": { name: "Tundra", collection: "toyota-tundra-2023" }
//         }
//       }
//     }
//   }
//
// Token + shop domain come from window.trekkerConfig (injected by theme.liquid).

const FITMENT_QUERY = `
  query GetFitments($first: Int!, $after: String) {
    metaobjects(type: "vehicle_fitment", first: $first, after: $after) {
      pageInfo { hasNextPage endCursor }
      nodes {
        fields {
          key
          value
          reference { ... on Collection { handle } }
        }
      }
    }
  }
`;

const VehicleData = {
  async getHierarchy() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      if (cached && (Date.now() - cached.ts) < CACHE_TTL && cached.entries) {
        console.info('[trekker:vehicle] Loaded hierarchy from sessionStorage cache (%d years).', Object.keys(cached.data).length);
        this._entries = cached.entries;
        return cached.data;
      }
    } catch { /* ignore */ }

    // Prefer Storefront API if token is configured; fall back to Liquid-rendered data
    const entries = window.trekkerConfig?.storefrontToken
      ? await this._fetchAllFitments()
      : (window.trekkerVehicleData || []);

    // Store flat entries so product-line selectors can look up all collections per vehicle
    this._entries = entries;

    const hierarchy = this._buildHierarchy(entries);

    const source = window.trekkerConfig?.storefrontToken ? 'Storefront API' : 'Liquid';
    console.info(
      '[trekker:vehicle] Hierarchy built from %s — %d fitment entries → %d years: %s',
      source,
      entries.length,
      Object.keys(hierarchy).length,
      Object.keys(hierarchy).sort((a, b) => Number(b) - Number(a)).join(', ') || '(none)'
    );

    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: hierarchy, entries }));
    } catch { /* ignore */ }

    return hierarchy;
  },

  async _fetchAllFitments() {
    const cfg = window.trekkerConfig;

    if (!cfg) {
      console.error('[trekker:vehicle] window.trekkerConfig is not defined. Make sure the Storefront API token is set in Online Store → Customize → Theme settings → Vehicle Search.');
      return [];
    }

    if (!cfg.storefrontToken) {
      console.error('[trekker:vehicle] storefrontToken is missing from window.trekkerConfig. Paste your Storefront API public token in Theme settings → Vehicle Search.');
      return [];
    }

    const endpoint = `https://${cfg.shop}/api/${cfg.storefrontVersion || '2026-04'}/graphql.json`;
    console.info('[trekker:vehicle] Fetching fitments from %s (token: %s…)', endpoint, cfg.storefrontToken.slice(0, 8));

    const headers  = {
      'Content-Type':                      'application/json',
      'X-Shopify-Storefront-Access-Token': cfg.storefrontToken,
    };

    const entries = [];
    let cursor  = null;
    let hasNext = true;
    let page    = 0;

    while (hasNext) {
      page++;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query: FITMENT_QUERY, variables: { first: 250, after: cursor } }),
      });

      if (res.status === 401 || res.status === 403) {
        console.error(
          '[trekker:vehicle] HTTP %d — token rejected. Check that your Storefront API token has the "unauthenticated_read_metaobjects" scope in Shopify Admin → Apps → (your app) → API credentials.',
          res.status
        );
        throw new Error(`Storefront API ${res.status} — check token scopes`);
      }

      if (!res.ok) {
        const body = await res.text().catch(() => '');
        console.error('[trekker:vehicle] HTTP %d — unexpected error.\n%s', res.status, body);
        throw new Error(`Storefront API ${res.status}`);
      }

      const json = await res.json();
      const { data, errors } = json;

      if (errors?.length) {
        errors.forEach(e => {
          const scopeHint = e.message?.toLowerCase().includes('access') || e.extensions?.code === 'ACCESS_DENIED'
            ? ' → Missing scope: unauthenticated_read_metaobjects'
            : '';
          console.error('[trekker:vehicle] GraphQL error:%s %s', scopeHint, e.message, e.extensions || '');
        });
        throw new Error(errors[0].message);
      }

      if (!data?.metaobjects) {
        console.error('[trekker:vehicle] Unexpected response shape — "metaobjects" field missing.', json);
        throw new Error('Unexpected Storefront API response');
      }

      const nodes = data.metaobjects.nodes;
      let skipped = 0;

      for (const node of nodes) {
        const f = {};
        for (const field of node.fields) {
          // collection field comes via reference.handle; all others via value
          f[field.key] = field.reference?.handle ?? field.value ?? '';
        }
        if (f.year && f.make_slug && f.model_slug) {
          entries.push(f);
        } else {
          skipped++;
        }
      }

      console.info('[trekker:vehicle] Page %d — %d nodes, %d valid, %d skipped (missing year/make_slug/model_slug).', page, nodes.length, nodes.length - skipped, skipped);

      hasNext = data.metaobjects.pageInfo.hasNextPage;
      cursor  = data.metaobjects.pageInfo.endCursor;
    }

    console.info('[trekker:vehicle] Fetch complete — %d total fitment entries across %d page(s).', entries.length, page);
    return entries;
  },

  _buildHierarchy(entries) {
    const h = {};
    for (const e of entries) {
      if (!h[e.year]) h[e.year] = {};
      if (!h[e.year][e.make_slug]) {
        h[e.year][e.make_slug] = { name: e.make || toDisplayName(e.make_slug), models: {} };
      }
      h[e.year][e.make_slug].models[e.model_slug] = {
        name:       e.model || toDisplayName(e.model_slug),
        collection: e.collection,
      };
    }
    return h;
  },

  /** Years present in the hierarchy, newest first */
  getYears(h) {
    return Object.keys(h).sort((a, b) => Number(b) - Number(a));
  },

  /** Makes for a given year — [{slug, name}] sorted by name */
  getMakes(h, year) {
    return Object.entries(h[year] || {})
      .map(([slug, d]) => ({ slug, name: d.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  /** Models for a given year + make — [{slug, name, collection}] sorted by name */
  getModels(h, year, makeSlug) {
    return Object.entries(h[year]?.[makeSlug]?.models || {})
      .map(([slug, d]) => ({ slug, name: d.name, collection: d.collection }))
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  /** Direct collection handle lookup */
  getCollection(h, year, makeSlug, modelSlug) {
    return h[year]?.[makeSlug]?.models?.[modelSlug]?.collection ?? null;
  },

  /**
   * Returns a hierarchy filtered to only include vehicles whose collections
   * contain at least one product of the given product_type.
   * Queries the Storefront API for products matching the type, collects their
   * collection handles, then filters the full hierarchy.
   */
  async getFilteredHierarchy(productType) {
    const full = await this.getHierarchy();
    if (!productType) return full;

    const cacheKey = `trekker-filtered-${productType}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey));
      if (cached && (Date.now() - cached.ts) < CACHE_TTL) {
        console.info('[trekker:vehicle] Filtered hierarchy for "%s" from cache (%d years).', productType, Object.keys(cached.data).length);
        return cached.data;
      }
    } catch { /* ignore */ }

    const collectionHandles = await this._fetchCollectionsByProductType(productType);
    if (!collectionHandles.size) {
      console.warn('[trekker:vehicle] No collections found for product type "%s".', productType);
      return full;
    }

    // Filter entries to only those whose collection is in the matching set
    const filtered = this._entries.filter(e => e.collection && collectionHandles.has(e.collection));
    const hierarchy = this._buildHierarchy(filtered);

    console.info('[trekker:vehicle] Filtered hierarchy for "%s" — %d/%d entries, %d years.',
      productType, filtered.length, this._entries.length, Object.keys(hierarchy).length);

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: hierarchy }));
    } catch { /* ignore */ }

    return hierarchy;
  },

  /**
   * Queries the Storefront API for all products of a given type and returns
   * a Set of collection handles those products belong to.
   */
  async _fetchCollectionsByProductType(productType) {
    const cfg = window.trekkerConfig;
    if (!cfg?.storefrontToken) return new Set();

    const endpoint = `https://${cfg.shop}/api/${cfg.storefrontVersion || '2026-04'}/graphql.json`;
    const headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': cfg.storefrontToken,
    };

    const query = `query FilterByType($first: Int!, $after: String, $query: String!) {
      products(first: $first, after: $after, query: $query) {
        pageInfo { hasNextPage endCursor }
        nodes {
          collections(first: 20) {
            nodes { handle }
          }
        }
      }
    }`;

    const handles = new Set();
    let cursor = null;
    let hasNext = true;

    while (hasNext) {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query,
          variables: { first: 250, after: cursor, query: `product_type:"${productType}" OR title:*${productType}*` }
        }),
      });

      if (!res.ok) break;
      const { data } = await res.json();
      if (!data?.products) break;

      for (const product of data.products.nodes) {
        for (const col of product.collections.nodes) {
          if (col.handle) handles.add(col.handle);
        }
      }

      hasNext = data.products.pageInfo.hasNextPage;
      cursor = data.products.pageInfo.endCursor;
    }

    console.info('[trekker:vehicle] Found %d collection(s) with product type "%s".', handles.size, productType);
    return handles;
  },

  /**
   * All collection handles for a given vehicle across every product line.
   * Uses the raw entries stored during getHierarchy() so product-line selectors
   * can try each collection until they find one matching their product type.
   */
  getCollectionsForVehicle(year, makeSlug, modelSlug) {
    if (!this._entries) return [];
    const seen = new Set();
    const collections = [];
    for (const e of this._entries) {
      if (e.year === year && e.make_slug === makeSlug && e.model_slug === modelSlug && e.collection) {
        if (!seen.has(e.collection)) {
          seen.add(e.collection);
          collections.push(e.collection);
        }
      }
    }
    return collections;
  },
};

// Expose so other sections (fitment-selector, etc.) can reuse without re-fetching
window.TrekkerVehicleData = VehicleData;

// ─── Garage store (localStorage) ─────────────────────────────────────────────

const GarageStore = {
  read() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || { vehicles: [], activeId: null };
    } catch {
      return { vehicles: [], activeId: null };
    }
  },

  write(data) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('on:garage:change', { detail: data, bubbles: true }));
  },

  getActive() {
    const { vehicles, activeId } = this.read();
    if (!activeId) return null;
    return vehicles.find(v => v.id === activeId) || null;
  },

  add(vehicle) {
    const data = this.read();
    if (data.vehicles.length >= MAX_VEHICLES) return false;
    vehicle.id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    data.vehicles.push(vehicle);
    data.activeId = vehicle.id;
    this.write(data);
    return true;
  },

  remove(id) {
    const data = this.read();
    data.vehicles = data.vehicles.filter(v => v.id !== id);
    if (data.activeId === id) data.activeId = data.vehicles[0]?.id || null;
    this.write(data);
  },

  setActive(id) {
    const data = this.read();
    data.activeId = id;
    this.write(data);
  }
};

// ─── VehicleGarage drawer ─────────────────────────────────────────────────────

if (!customElements.get('vehicle-garage')) {
  class VehicleGarage extends SideDrawer {
    connectedCallback() {
      this.listEl      = this.querySelector('.vehicle-garage__list');
      this.addSection  = this.querySelector('.vehicle-garage__add-section');
      this.addBtn      = this.querySelector('.js-add-vehicle-btn');
      this.addForm     = this.querySelector('.vehicle-garage__add-form');
      this.yearSel     = this.querySelector('[data-vehicle-year]');
      this.makeSel     = this.querySelector('[data-vehicle-make]');
      this.modelSel    = this.querySelector('[data-vehicle-model]');
      this.statusEl    = this.querySelector('.vehicle-garage__status');

      this._hierarchy  = null; // loaded on first open

      this._renderList();
      this._updateHeaderBadge();
      this._bindEvents();

      window.addEventListener('on:garage:change', () => {
        this._renderList();
        this._updateHeaderBadge();
      });
    }

    open(...args) {
      if (GarageStore.read().vehicles.length > 0) this.addSection.hidden = true;
      super.open(...args);
      requestAnimationFrame(() => {
        const activeItem = this.listEl.querySelector('.vehicle-garage__item.is-active');
        if (activeItem) activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }

    _bindEvents() {
      // Toggle add form — load vehicle data on first open
      this.addBtn?.addEventListener('click', () => {
        const opening = this.addSection.hidden;
        this.addSection.hidden = !opening;
        if (opening) this._initVehicleData();
      });

      this.yearSel.addEventListener('change',  () => this._onYearChange());
      this.makeSel.addEventListener('change',  () => this._onMakeChange());

      this.addForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this._saveVehicle();
      });

      this.listEl.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.js-remove-vehicle');
        const selectBtn = e.target.closest('.js-select-vehicle');
        if (removeBtn) GarageStore.remove(removeBtn.dataset.id);
        if (selectBtn) {
          const id = selectBtn.dataset.id;
          GarageStore.setActive(id);
          this.close();
          const vehicle = GarageStore.read().vehicles.find(v => v.id === id);
          if (vehicle) {
            if (vehicle.collection) {
              window.location.href = `/collections/${vehicle.collection}`;
            } else {
              const makeSlug  = vehicle.makeSlug  || toSlug(vehicle.make);
              const modelSlug = vehicle.modelSlug || toSlug(vehicle.model);
              const url = new URL('/collections/all', window.location.origin);
              url.searchParams.set('filter.p.tag', `${FIT_PREFIX}${vehicle.year}:${makeSlug}:${modelSlug}`);
              window.location.href = url.toString();
            }
          }
        }
      });
    }

    // ── Data loading ──────────────────────────────────────────────────────────

    async _initVehicleData() {
      if (this._hierarchy) {
        this._populateYears();
        return;
      }

      this._setSelectState(this.yearSel, 'Loading vehicles…', true);
      this._setSelectState(this.makeSel, 'Select year first', true);
      this._setSelectState(this.modelSel, 'Select make first', true);

      try {
        this._hierarchy = await VehicleData.getHierarchy();

        if (!Object.keys(this._hierarchy).length) {
          this._setSelectState(this.yearSel, 'No vehicles found', true);
          this._showStatus(
            'No fit: tags found on products. Tag products as fit:YEAR:MAKE:MODEL (e.g. fit:2023:toyota:tundra) to populate this selector.',
            true
          );
          return;
        }

        this._populateYears();
      } catch (err) {
        this._setSelectState(this.yearSel, 'Error loading data', true);
        this._showStatus('Could not load vehicle data. Please try again.', true);
        console.error('[vehicle-garage]', err);
      }
    }

    _populateYears() {
      const years = VehicleData.getYears(this._hierarchy);
      this.yearSel.innerHTML = '<option value="">Select year</option>';
      years.forEach(y => {
        this.yearSel.insertAdjacentHTML('beforeend', `<option value="${y}">${y}</option>`);
      });
      this.yearSel.disabled = false;
      this._showStatus('');
    }

    _onYearChange() {
      const year = this.yearSel.value;
      this._setSelectState(this.makeSel,  'Select make first',  true);
      this._setSelectState(this.modelSel, 'Select make first', true);
      if (!year || !this._hierarchy) return;

      const makes = VehicleData.getMakes(this._hierarchy, year);
      this.makeSel.innerHTML = '<option value="">Select make</option>';
      makes.forEach(m => {
        this.makeSel.insertAdjacentHTML(
          'beforeend',
          `<option value="${this._esc(m.slug)}">${this._esc(m.name)}</option>`
        );
      });
      this.makeSel.disabled = false;
    }

    _onMakeChange() {
      const year = this.yearSel.value;
      const make = this.makeSel.value;
      this._setSelectState(this.modelSel, 'Select model', true);
      if (!year || !make || !this._hierarchy) return;

      const models = VehicleData.getModels(this._hierarchy, year, make);
      this.modelSel.innerHTML = '<option value="">Select model</option>';
      models.forEach(m => {
        this.modelSel.insertAdjacentHTML(
          'beforeend',
          `<option value="${this._esc(m.slug)}">${this._esc(m.name)}</option>`
        );
      });
      this.modelSel.disabled = false;
    }

    // ── Save vehicle ──────────────────────────────────────────────────────────

    _saveVehicle() {
      const year      = this.yearSel.value;
      const makeSlug  = this.makeSel.value;
      const modelSlug = this.modelSel.value;

      if (!year || !makeSlug || !modelSlug) {
        this._showStatus('Please select a year, make, and model.', true);
        return;
      }

      const makeEntry  = VehicleData.getMakes(this._hierarchy, year).find(m => m.slug === makeSlug);
      const modelEntry = VehicleData.getModels(this._hierarchy, year, makeSlug).find(m => m.slug === modelSlug);

      const success = GarageStore.add({
        year,
        makeSlug,
        make:       makeEntry?.name  || toDisplayName(makeSlug),
        modelSlug,
        model:      modelEntry?.name || toDisplayName(modelSlug),
        collection: modelEntry?.collection || null,
      });

      if (!success) {
        this._showStatus(`Garage is full (max ${MAX_VEHICLES}). Remove a vehicle to add another.`, true);
        return;
      }

      // Redirect to vehicle's collection then close
      const collection = modelEntry?.collection || null;
      if (collection) {
        window.location.href = `/collections/${collection}`;
      } else {
        const url = new URL('/collections/all', window.location.origin);
        url.searchParams.set('filter.p.tag', `${FIT_PREFIX}${year}:${makeSlug}:${modelSlug}`);
        window.location.href = url.toString();
      }
    }

    // ── Render saved vehicles ─────────────────────────────────────────────────

    _renderList() {
      const { vehicles, activeId } = GarageStore.read();

      if (vehicles.length === 0) {
        this.listEl.innerHTML = '';
        if (this.addBtn) this.addBtn.hidden = true;
        this.addSection.hidden = false;
        this._initVehicleData();
        return;
      }

      this.listEl.innerHTML = vehicles.map(v => {
        const isActive = v.id === activeId;
        return `
          <div class="vehicle-garage__item${isActive ? ' is-active' : ''}" data-id="${v.id}">
            <div class="vehicle-garage__item-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="13" viewBox="35 188 735 310" overflow="visible"><path fill="none" stroke="currentColor" stroke-miterlimit="10" vector-effect="non-scaling-stroke" stroke-width="2" d="M703.31,406.52h24.5c.91,0,1.8-.24,2.59-.71l22.17-13.05c1.58-2.76,2.85-4.98,4.43-7.75,0,0-.06-15.6,0-20.78.02-1.58-16.83-4.01-16.83-4.01-2.03-.48-3.57-2.16-3.87-4.23l-6.03-41.6-12.06-12.22-150.99-8.29c-5.45-.3-10.32-3.53-12.72-8.43l-46.73-95.67c-1.88-3.85-5.52-6.54-9.75-7.21l-89.27-14.12c-.98-.16-1.97-.25-2.96-.27l-303.83-8.32c-7.61-.21-14.04,5.59-14.62,13.17,0,0-6.6,83.14-6.99,90.72-.11,2.12,3.01,5.81,3.06,7.93.28,11.51-3.07,38.6-3.07,38.6l-1.88,23.69c-.07.93-.4,1.82-.94,2.57l-12.61,17.55c-.63.87-.96,1.92-.96,3l.08,16c0,1.15.4,2.26,1.11,3.16l16.8,21.04c.93,1.17,2.33,1.87,3.82,1.92l44.88,1.48"/><path fill="none" stroke="currentColor" stroke-miterlimit="10" vector-effect="non-scaling-stroke" stroke-width="2" d="M83.25,225.17l-35.26,2.6c-7.33.54-13,6.64-13,13.99v84.77c0,6.73,4.78,12.51,11.39,13.78l25,4.78"/><path fill="none" stroke="currentColor" stroke-miterlimit="10" vector-effect="non-scaling-stroke" stroke-width="2" d="M479.27,211.26l31.13,62.23c1.26,2.52-.57,5.49-3.39,5.49h-118.96c-2.1,0-3.8-1.7-3.8-3.8v-73.98c0-2.3,2.02-4.07,4.3-3.76l87.83,11.74c1.25.17,2.33.94,2.89,2.06Z"/><path fill="none" stroke="currentColor" stroke-miterlimit="10" vector-effect="non-scaling-stroke" stroke-width="2" d="M353.46,196.76v79.56c0,1.47-1.2,2.65-2.67,2.62l-152.87-2.95c-1.43-.03-2.57-1.19-2.57-2.62v-78.91c0-1.46,1.2-2.64,2.66-2.62l152.87,2.3c1.43.02,2.58,1.19,2.58,2.62Z"/><line fill="none" stroke="currentColor" stroke-miterlimit="10" vector-effect="non-scaling-stroke" stroke-width="2" x1="540.02" y1="319.28" x2="80.77" y2="309.48"/><line fill="none" stroke="currentColor" stroke-miterlimit="10" vector-effect="non-scaling-stroke" stroke-width="2" x1="251.83" y1="401.33" x2="580.26" y2="401.33"/><circle fill="none" stroke="currentColor" stroke-miterlimit="10" vector-effect="non-scaling-stroke" stroke-width="2" cx="191.12" cy="422.33" r="64.26"/><circle fill="none" stroke="currentColor" stroke-miterlimit="10" vector-effect="non-scaling-stroke" stroke-width="2" cx="641.01" cy="422.33" r="64.26"/></svg>
            </div>
            <div class="vehicle-garage__item-info">
              <span class="vehicle-garage__item-name">${this._esc(v.year)} ${this._esc(v.make)} ${this._esc(v.model)}</span>
              ${isActive ? '<span class="vehicle-garage__active-badge">Active</span>' : ''}
            </div>
            <div class="vehicle-garage__item-actions">
              ${!isActive
                ? `<button type="button" class="js-select-vehicle btn btn--small btn--secondary" data-id="${v.id}">Select</button>`
                : ''}
              <button type="button" class="js-remove-vehicle vehicle-garage__remove-btn" data-id="${v.id}"
                aria-label="Remove ${this._esc(v.year)} ${this._esc(v.make)} ${this._esc(v.model)}">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                </svg>
              </button>
            </div>
            ${v.collection ? `<a href="/collections/${this._esc(v.collection)}" class="vehicle-garage__shop-link">Shop for this vehicle &rarr;</a>` : ''}
          </div>`;
      }).join('');

      if (this.addBtn) this.addBtn.hidden = vehicles.length >= MAX_VEHICLES;
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    _updateHeaderBadge() {
      const { vehicles } = GarageStore.read();
      const badge = document.getElementById('vehicle-garage-badge');
      if (!badge) return;
      badge.hidden = vehicles.length === 0;
      const countEl = badge.querySelector('.vehicle-garage-badge__count');
      if (countEl) countEl.textContent = vehicles.length;
    }

    _setSelectState(select, label, disabled) {
      select.innerHTML = `<option value="">${label}</option>`;
      select.disabled  = disabled;
    }

    _showStatus(msg, isError = false) {
      if (!this.statusEl) return;
      this.statusEl.textContent = msg;
      this.statusEl.hidden      = !msg;
      this.statusEl.className   = `vehicle-garage__status text-sm${isError ? ' vehicle-garage__status--error' : ''}`;
    }

    _esc(str) {
      return String(str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
  }

  customElements.define('vehicle-garage', VehicleGarage);
}

// ─── VehicleContextBar ────────────────────────────────────────────────────────

if (!customElements.get('vehicle-context-bar')) {
  class VehicleContextBar extends HTMLElement {
    connectedCallback() {
      this._render();
      window.addEventListener('on:garage:change', () => this._render());

      this.addEventListener('click', (e) => {
        if (e.target.closest('.js-open-garage')) {
          document.getElementById('vehicle-garage')?.open(e.target.closest('.js-open-garage'));
        }
        const filterBtn = e.target.closest('.js-filter-by-vehicle');
        if (filterBtn && !filterBtn.dataset.active) {
          e.preventDefault();
          GarageStore.setActive(null);
          window.location.href = filterBtn.href || '/collections/all';
        }
      });
    }

    _render() {
      const active = GarageStore.getActive();

      if (!active) { this.hidden = true; return; }

      this.hidden = false;

      const vehicleEl = this.querySelector('.vehicle-context-bar__vehicle');
      if (vehicleEl) vehicleEl.textContent = `${active.year} ${active.make} ${active.model}`;

      // Tag: fit:YEAR:MAKE-SLUG:MODEL-SLUG  — matches the format on products
      const filterLink = this.querySelector('.js-filter-by-vehicle');
      if (!filterLink) return;

      // Prefer dedicated collection; fall back to tag filter on collections/all
      if (active.collection) {
        const collectionUrl = `/collections/${active.collection}`;
        const onVehiclePLP  = window.location.pathname === collectionUrl;
        filterLink.textContent = onVehiclePLP ? 'Clear vehicle filter' : 'Shop for this vehicle';
        filterLink.href        = onVehiclePLP ? '/collections/all' : collectionUrl;
        if (onVehiclePLP) delete filterLink.dataset.active;
        else filterLink.dataset.active = 'true';
      } else {
        // Legacy tag-based fallback
        const makeSlug  = active.makeSlug  || toSlug(active.make);
        const modelSlug = active.modelSlug || toSlug(active.model);
        const tag       = `${FIT_PREFIX}${active.year}:${makeSlug}:${modelSlug}`;
        const url       = new URL(window.location.href);
        const currentTag = url.searchParams.get('filter.p.tag');
        if (currentTag === tag) {
          filterLink.textContent = 'Clear vehicle filter';
          url.searchParams.delete('filter.p.tag');
          delete filterLink.dataset.active;
        } else {
          filterLink.textContent = 'Shop for this vehicle';
          url.searchParams.set('filter.p.tag', tag);
          filterLink.dataset.active = 'true';
        }
        filterLink.href = url.toString();
      }
    }
  }

  customElements.define('vehicle-context-bar', VehicleContextBar);
}

// ─── VsDropdown — custom styled dropdown for desktop vehicle search ──────────
//
// Wraps a <div class="vs-dropdown"> element.
// Fires a native 'change' event on the hidden <select data-vs-*> when a value
// is picked, so VehicleSearchBar's existing cascade logic stays unchanged.

class VsDropdown {
  constructor(containerEl) {
    this.el       = containerEl;
    this.trigger  = containerEl.querySelector('.vs-dropdown__trigger');
    this.listEl   = containerEl.querySelector('.vs-dropdown__list');
    this.valueEl  = containerEl.querySelector('.vs-dropdown__value');
    this._value   = '';
    this._onChange = null; // callback(value)
    this._bindEvents();
  }

  get value() { return this._value; }

  /** Populate list with [{value, label}] and enable the trigger */
  setOptions(options) {
    this.listEl.innerHTML = '';
    options.forEach(({ value, label }) => {
      const li = document.createElement('li');
      li.className = 'vs-dropdown__item';
      li.setAttribute('role', 'option');
      li.setAttribute('data-value', value);
      li.setAttribute('aria-selected', value === this._value ? 'true' : 'false');
      li.textContent = label;
      this.listEl.appendChild(li);
    });
    this.trigger.disabled = false;
  }

  /** Reset to placeholder state */
  reset(placeholder) {
    this._value = '';
    this.listEl.innerHTML = '';
    this.valueEl.textContent = placeholder;
    this.valueEl.setAttribute('data-empty', '');
    this.trigger.disabled = true;
    this.close();
  }

  /** Set loading placeholder without disabling (shows spinner text) */
  setLoading(text) {
    this._value = '';
    this.listEl.innerHTML = '';
    this.valueEl.textContent = text;
    this.valueEl.setAttribute('data-empty', '');
    this.trigger.disabled = true;
    this.close();
  }

  open() {
    // Close any other open dropdowns on the page
    document.querySelectorAll('.vs-dropdown[data-open]').forEach(d => {
      if (d !== this.el) d._vsdInstance?.close();
    });
    this.el.dataset.open = '';
    this.trigger.setAttribute('aria-expanded', 'true');
    this.listEl.removeAttribute('hidden');
    // Scroll selected item into view
    const active = this.listEl.querySelector('[aria-selected="true"]');
    if (active) active.scrollIntoView({ block: 'nearest' });
  }

  close() {
    delete this.el.dataset.open;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.listEl.setAttribute('hidden', '');
  }

  _select(value, label) {
    this._value = value;
    this.valueEl.textContent = label;
    this.valueEl.removeAttribute('data-empty');
    this.listEl.querySelectorAll('.vs-dropdown__item').forEach(li => {
      li.setAttribute('aria-selected', li.dataset.value === value ? 'true' : 'false');
    });
    this.close();
    if (this._onChange) this._onChange(value);
  }

  _bindEvents() {
    // Store ref on DOM element so other instances can close this
    this.el._vsdInstance = this;

    // Toggle open/close on trigger click
    this.trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.trigger.disabled) return;
      this.el.dataset.open !== undefined && 'open' in this.el.dataset
        ? this.close()
        : this.open();
    });

    // Item selection
    this.listEl.addEventListener('click', (e) => {
      const item = e.target.closest('.vs-dropdown__item');
      if (!item) return;
      this._select(item.dataset.value, item.textContent.trim());
    });

    // Keyboard: arrow keys + enter + escape
    this.trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!('open' in this.el.dataset)) { this.open(); return; }
        const items = [...this.listEl.querySelectorAll('.vs-dropdown__item')];
        const focused = this.listEl.querySelector('.vs-dropdown__item--focus');
        let idx = items.indexOf(focused);
        idx = e.key === 'ArrowDown' ? Math.min(idx + 1, items.length - 1) : Math.max(idx - 1, 0);
        if (idx < 0) idx = 0;
        items.forEach(i => i.classList.remove('vs-dropdown__item--focus'));
        items[idx]?.classList.add('vs-dropdown__item--focus');
        items[idx]?.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        const focused = this.listEl.querySelector('.vs-dropdown__item--focus');
        if (focused) { e.preventDefault(); this._select(focused.dataset.value, focused.textContent.trim()); }
        else if (!('open' in this.el.dataset)) this.open();
      } else if (e.key === 'Escape') {
        this.close();
        this.trigger.focus();
      }
    });

    // Close when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.el.contains(e.target)) this.close();
    });
  }
}

// ─── VehicleSearchBar (homepage hero search — WeatherTech-style) ─────────────
//
// Renders as a standalone section on the homepage.
// Loads vehicle data immediately on mount, cascades Year → Make → Model,
// saves the selection to the garage, then redirects to the results page.

if (!customElements.get('vehicle-search-bar')) {
  class VehicleSearchBar extends HTMLElement {
    connectedCallback() {
      this.redirectTo  = this.dataset.redirect || '/collections/all';
      this._hierarchy  = null;

      // ── Desktop custom dropdowns ──
      const yearDropEl  = this.querySelector('[data-vs-year-drop]');
      const makeDropEl  = this.querySelector('[data-vs-make-drop]');
      const modelDropEl = this.querySelector('[data-vs-model-drop]');

      this.d = {
        // VsDropdown instances (desktop)
        yearDrop:  yearDropEl  ? new VsDropdown(yearDropEl)  : null,
        makeDrop:  makeDropEl  ? new VsDropdown(makeDropEl)  : null,
        modelDrop: modelDropEl ? new VsDropdown(modelDropEl) : null,
        // Hidden selects (still used as value store + change event source)
        year:   this.querySelector('[data-vs-year]'),
        make:   this.querySelector('[data-vs-make]'),
        model:  this.querySelector('[data-vs-model]'),
        btn:    this.querySelector('.vehicle-search__form-wrap .vehicle-search__btn'),
        status: this.querySelector('.vehicle-search__form-wrap ~ .vehicle-search__status')
          || this.querySelector('.vehicle-search__status'),
      };

      // Wire VsDropdown onChange → update hidden select + trigger cascade
      if (this.d.yearDrop) this.d.yearDrop._onChange = (v) => {
        if (this.d.year) { this.d.year.value = v; }
        this._onYearChange(this.d);
      };
      if (this.d.makeDrop) this.d.makeDrop._onChange = (v) => {
        if (this.d.make) { this.d.make.value = v; }
        this._onMakeChange(this.d);
      };
      if (this.d.modelDrop) this.d.modelDrop._onChange = (v) => {
        if (this.d.model) { this.d.model.value = v; }
        this._onModelChange(this.d);
      };

      // ── Mobile sheet VsDropdowns ──
      const mYearDropEl  = this.querySelector('[data-vs-m-year-drop]');
      const mMakeDropEl  = this.querySelector('[data-vs-m-make-drop]');
      const mModelDropEl = this.querySelector('[data-vs-m-model-drop]');

      this.m = {
        yearDrop:  mYearDropEl  ? new VsDropdown(mYearDropEl)  : null,
        makeDrop:  mMakeDropEl  ? new VsDropdown(mMakeDropEl)  : null,
        modelDrop: mModelDropEl ? new VsDropdown(mModelDropEl) : null,
        btn:    this.querySelector('.js-vs-sheet-submit'),
        status: this.querySelector('.vehicle-search__sheet-body .vehicle-search__status'),
      };

      // Wire VsDropdown onChange for mobile
      if (this.m.yearDrop)  this.m.yearDrop._onChange  = () => this._onYearChange(this.m);
      if (this.m.makeDrop)  this.m.makeDrop._onChange  = () => this._onMakeChange(this.m);
      if (this.m.modelDrop) this.m.modelDrop._onChange = () => this._onModelChange(this.m);

      // ── Modal elements ──
      this.trigger  = this.querySelector('.js-vs-trigger');
      this.overlay  = this.querySelector('.js-vs-overlay');
      this.sheet    = this.querySelector('.vehicle-search__sheet');
      this.closeBtn = this.querySelector('.js-vs-close');

      this._loadData();
      this._bindEvents();
    }

    // ── Data loading ──────────────────────────────────────────────────────────

    async _loadData() {
      this._setLoadingState(true);
      try {
        this._hierarchy = await VehicleData.getHierarchy();

        if (!Object.keys(this._hierarchy).length) {
          this._setStatus('No vehicle data found. Tag products as fit:YEAR:MAKE:MODEL.', true);
          return;
        }

        this._populateYears(this.d.yearDrop || this.d.year);
        this._populateYears(this.m.yearDrop);
      } catch (err) {
        this._setStatus('Could not load vehicle data. Please refresh.', true);
        console.error('[vehicle-search-bar]', err);
      } finally {
        this._setLoadingState(false);
      }
    }

    _populateYears(target) {
      if (!target) return;
      const years = VehicleData.getYears(this._hierarchy);
      // VsDropdown (desktop)
      if (target instanceof VsDropdown) {
        target.setOptions(years.map(y => ({ value: y, label: y })));
        return;
      }
      // Native select (mobile)
      target.innerHTML = '<option value="">Select Year</option>';
      years.forEach(y => target.insertAdjacentHTML('beforeend', `<option value="${y}">${y}</option>`));
      target.disabled = false;
      target.classList.remove('vehicle-search__select--loading');
    }

    // ── Events ────────────────────────────────────────────────────────────────

    _bindEvents() {
      // Desktop cascades are handled by VsDropdown._onChange callbacks (see connectedCallback)
      this.querySelector('.vehicle-search__form')?.addEventListener('submit', (e) => {
        e.preventDefault();
        this._onSubmit(this.d);
      });

      // Mobile sheet CTA
      this.m.btn?.addEventListener('click', () => this._onSubmit(this.m));

      // "My Vehicles" → open garage drawer
      this.querySelector('.js-open-garage')?.addEventListener('click', () => {
        document.getElementById('vehicle-garage')?.open(this.querySelector('.js-open-garage'));
      });

      // Mobile modal open / close
      this.trigger?.addEventListener('click',  () => this._openSheet());
      this.overlay?.addEventListener('click',  () => this._closeSheet());
      this.closeBtn?.addEventListener('click', () => this._closeSheet());

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.hasAttribute('open')) this._closeSheet();
      });
    }

    _onYearChange(sel) {
      const year = sel.yearDrop ? sel.yearDrop.value : sel.year?.value;
      if (sel.makeDrop)  sel.makeDrop.reset('Select Make');
      else this._resetSelect(sel.make, 'Select Make', true);
      if (sel.modelDrop) sel.modelDrop.reset('Select Model');
      else this._resetSelect(sel.model, 'Select Model', true);
      if (sel.btn) sel.btn.disabled = true;
      if (!year || !this._hierarchy) return;

      // getMakes now returns [{slug, name}]
      const makes = VehicleData.getMakes(this._hierarchy, year);
      if (sel.makeDrop) {
        sel.makeDrop.setOptions(makes.map(m => ({ value: m.slug, label: m.name })));
      } else {
        sel.make.innerHTML = '<option value="">Select Make</option>';
        makes.forEach(m => sel.make.insertAdjacentHTML('beforeend', `<option value="${m.slug}">${m.name}</option>`));
        sel.make.disabled = false;
      }
    }

    _onMakeChange(sel) {
      const year = sel.yearDrop ? sel.yearDrop.value : sel.year?.value;
      const make = sel.makeDrop ? sel.makeDrop.value : sel.make?.value;
      if (sel.modelDrop) sel.modelDrop.reset('Select Model');
      else this._resetSelect(sel.model, 'Select Model', true);
      if (sel.btn) sel.btn.disabled = true;
      if (!year || !make || !this._hierarchy) return;

      // getModels now returns [{slug, name, collection}]
      const models = VehicleData.getModels(this._hierarchy, year, make);
      if (sel.modelDrop) {
        sel.modelDrop.setOptions(models.map(m => ({ value: m.slug, label: m.name })));
      } else {
        sel.model.innerHTML = '<option value="">Select Model</option>';
        models.forEach(m => sel.model.insertAdjacentHTML('beforeend', `<option value="${m.slug}">${m.name}</option>`));
        sel.model.disabled = false;
      }
    }

    _onModelChange(sel) {
      const hasModel = sel.modelDrop ? sel.modelDrop.value : sel.model?.value;
      if (sel.btn) sel.btn.disabled = !hasModel;
    }

    _onSubmit(sel) {
      const year      = sel.yearDrop  ? sel.yearDrop.value  : sel.year?.value;
      const makeSlug  = sel.makeDrop  ? sel.makeDrop.value  : sel.make?.value;
      const modelSlug = sel.modelDrop ? sel.modelDrop.value : sel.model?.value;
      if (!year || !makeSlug || !modelSlug) return;

      // Look up the make display name from hierarchy
      const makeEntry  = VehicleData.getMakes(this._hierarchy, year).find(m => m.slug === makeSlug);
      const modelEntry = VehicleData.getModels(this._hierarchy, year, makeSlug).find(m => m.slug === modelSlug);
      const makeName   = makeEntry?.name  || toDisplayName(makeSlug);
      const modelName  = modelEntry?.name || toDisplayName(modelSlug);
      const collection = modelEntry?.collection || null;

      GarageStore.add({ year, makeSlug, make: makeName, modelSlug, model: modelName, collection });

      // Redirect to dedicated collection if available, otherwise fall back to tag filter
      if (collection) {
        window.location.href = `/collections/${collection}`;
      } else {
        const dest = new URL(this.redirectTo, window.location.origin);
        dest.searchParams.set('filter.p.tag', `${FIT_PREFIX}${year}:${makeSlug}:${modelSlug}`);
        window.location.href = dest.toString();
      }
    }

    // ── Mobile sheet open / close ─────────────────────────────────────────────

    _openSheet() {
      this.setAttribute('open', '');
      this.sheet?.setAttribute('aria-hidden', 'false');
      this.overlay?.setAttribute('aria-hidden', 'false');
      document.body.classList.add('vs-open');
      // Focus year trigger after sheet animation
      setTimeout(() => this.m.yearDrop?.trigger?.focus(), 350);
    }

    _closeSheet() {
      this.removeAttribute('open');
      this.sheet?.setAttribute('aria-hidden', 'true');
      this.overlay?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('vs-open');
      this.trigger?.focus();
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    _setLoadingState(on) {
      if (on) {
        this.d.yearDrop?.setLoading('Select Year');
        this.m.yearDrop?.setLoading('Select Year');
      }
    }

    _resetSelect(sel, label, disabled) {
      if (!sel) return;
      sel.innerHTML = `<option value="">${label}</option>`;
      sel.disabled  = disabled;
    }

    _setStatus(msg, isError = false) {
      [this.d.status, this.m.status].forEach(el => {
        if (!el) return;
        el.textContent = msg;
        el.hidden      = !msg;
        el.className   = `vehicle-search__status${isError ? ' vehicle-search__status--error' : ''}`;
      });
    }
  }

  customElements.define('vehicle-search-bar', VehicleSearchBar);
}

// ─── Can't find your vehicle — garage drawer + vehicle search strip ──────────
//
// Handles toggle accordion + iframe form submission for all .js-cant-find-toggle
// and .js-cant-find-form instances (garage drawer, search bar desktop, mobile sheet).
// The popup uses its own separate handler in vehicle-modal.js.

document.addEventListener('click', (e) => {
  const toggle = e.target.closest('.js-cant-find-toggle');
  if (!toggle) return;
  const expanded = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!expanded));
  const bodyId = toggle.getAttribute('aria-controls');
  const body = bodyId ? document.getElementById(bodyId) : null;
  if (body) body.hidden = expanded;
});

document.addEventListener('submit', (e) => {
  const form = e.target.closest('.js-cant-find-form');
  if (!form) return;
  e.preventDefault();
  e.stopPropagation();

  const email   = form.querySelector('[name="contact[email]"]');
  const vehicle = form.querySelector('[name="contact[body]"]');
  const body    = form.closest('[data-cant-find-body]');
  const statusEl = body?.querySelector('[data-cant-find-status]');

  const setStatus = (msg, type) => {
    if (!statusEl) return;
    statusEl.textContent = msg;
    statusEl.hidden = false;
    statusEl.className = `cant-find-status cant-find-status--${type}`;
  };

  if (!email?.value || !vehicle?.value) {
    setStatus('Please fill in your email and vehicle info.', 'error');
    return;
  }

  const submitBtn = form.querySelector('[type="submit"]');
  if (submitBtn) submitBtn.disabled = true;

  const iframeName = 'cf-' + Date.now();
  const iframe = document.createElement('iframe');
  iframe.name = iframeName;
  iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);
  form.target = iframeName;

  iframe.addEventListener('load', () => {
    form.hidden = true;
    setStatus("Thanks! We'll be in touch soon.", 'success');
    iframe.remove();
    form.target = '';
  }, { once: true });

  form.submit();
});

// ─── Header opener + any a[href="#vehicle-garage"] link ──────────────────────

document.addEventListener('DOMContentLoaded', () => {
  const opener = document.getElementById('vehicle-garage-opener');
  const drawer = document.getElementById('vehicle-garage');
  if (opener && drawer) opener.addEventListener('click', () => drawer.open(opener));

  // Allow any link with href="#vehicle-garage" (e.g. hero CTA) to open the drawer
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href="#vehicle-garage"]');
    if (!link || !drawer) return;
    e.preventDefault();
    drawer.open(link);
  });

  // Initialise badge from stored garage on page load
  const { vehicles } = GarageStore.read();
  const badge = document.getElementById('vehicle-garage-badge');
  if (badge) {
    badge.hidden = vehicles.length === 0;
    const countEl = badge.querySelector('.vehicle-garage-badge__count');
    if (countEl) countEl.textContent = vehicles.length;
  }
});
